import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

export function BottomSheetModal({ visible, onClose, title, children }) {
  const [render, setRender] = useState(false);
  const [open, setOpen] = useState(false);
  const sheetRef = useRef(null);

  const startY = useRef(0);
  const currentY = useRef(0);
  const dragging = useRef(false);

  /* -------------------- OPEN / CLOSE -------------------- */
  useEffect(() => {
    if (visible) {
      setRender(true);
      setOpen(false);

      // FIRST FRAME: mounted off-screen
      requestAnimationFrame(() => {
        // SECOND FRAME: trigger transition
        requestAnimationFrame(() => {
          setOpen(true);
        });
      });
    } else {
      setOpen(false);

      const t = setTimeout(() => setRender(false), 250);
      return () => clearTimeout(t);
    }
  }, [visible]);

  /* -------------------- CLOSE -------------------- */
  const handleClose = () => {
    setOpen(false);
    setTimeout(() => onClose?.(), 250);
  };

  /* -------------------- DRAG -------------------- */
  const onPointerDown = (e) => {
    dragging.current = true;
    startY.current = e.clientY;
  };

  const onPointerMove = (e) => {
    if (!dragging.current || !sheetRef.current) return;

    const delta = e.clientY - startY.current;
    if (delta > 0) {
      currentY.current = delta;
      sheetRef.current.style.transition = 'none';
      sheetRef.current.style.transform = `translateY(${delta}px)`;
    }
  };

  const onPointerUp = () => {
    dragging.current = false;

    if (sheetRef.current) {
      sheetRef.current.style.transition = 'transform 250ms ease-out';
    }

    if (currentY.current > 120) {
      handleClose();
    } else {
      sheetRef.current.style.transform = 'translateY(0px)';
    }

    currentY.current = 0;
  };

  if (!render) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center">
      {/* Backdrop */}
      <div
        className={clsx(
          'absolute inset-0 bg-black transition-opacity duration-300',
          open ? 'opacity-40' : 'opacity-0'
        )}
        onClick={handleClose}
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className="relative w-full max-w-lg mx-auto bg-white rounded-3xl shadow-xl"
        style={{
          maxHeight: '60vh',
          bottom: '52px',
          marginLeft: '12px',
          marginRight: '12px',
          transform: open ? 'translateY(0%)' : 'translateY(110%)',
          transition: 'transform 300ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        {/* Handle */}
        <div
          className="flex justify-center pt-3 pb-3 cursor-grab"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          <div className="w-11 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="px-5 pb-2 relative">
          {title && <h2 className="text-lg font-semibold text-gray-900">{title}</h2>}

          <button
            onClick={handleClose}
            className="absolute right-4 top-0 w-9 h-9 flex items-center justify-center text-gray-700"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="px-5 pb-6 overflow-y-auto" style={{ maxHeight: '50vh' }}>
          {children}
        </div>
      </div>
    </div>
  );
}
